import * as React from "react";

/**
 * One polite and one assertive live region for the whole shell (CAP-10).
 * Fed by `announce()` from @kovernow/flows via `subscribe`; react-ui does
 * not depend on flows. Each message goes to exactly one region, and the same
 * message repeated within `dedupeMs` is dropped (no double announcements).
 */

export type Politeness = "polite" | "assertive";

export interface Announcement {
  message: string;
  politeness: Politeness;
}

export interface LiveAnnouncerProps {
  subscribe: (listener: (a: Announcement) => void) => () => void;
  dedupeMs?: number;
}

function LiveAnnouncer({ subscribe, dedupeMs = 1000 }: LiveAnnouncerProps) {
  const [polite, setPolite] = React.useState("");
  const [assertive, setAssertive] = React.useState("");
  const last = React.useRef<{ message: string; at: number } | null>(null);

  React.useEffect(
    () =>
      subscribe(({ message, politeness }) => {
        const now = Date.now();
        if (last.current && last.current.message === message && now - last.current.at < dedupeMs) {
          return;
        }
        last.current = { message, at: now };
        const set = politeness === "assertive" ? setAssertive : setPolite;
        // Clear first so the same text announced later is read again.
        set("");
        setTimeout(() => set(message), 50);
      }),
    [subscribe, dedupeMs],
  );

  return (
    <>
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true" data-testid="announcer-polite">
        {polite}
      </div>
      <div className="sr-only" role="alert" aria-live="assertive" aria-atomic="true" data-testid="announcer-assertive">
        {assertive}
      </div>
    </>
  );
}

export { LiveAnnouncer };
